import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { emptyDecayCalibration, IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IDecayQuery, IDecayResult } from '@models/decay/decay-estimate';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { DecayCalibrationTypeService } from '@services/decay/decay-calibration-type.service';
import { DecayCalibrationService } from '@services/decay/decay-calibration.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MenuService } from '@services/menu.service';
import { clone, createObservationsArray } from '@shared/arrays';
import { filterNegativesFromResult, processDecayBalanceResult } from '@shared/decay-helpers';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ObservableStore } from '../observable-store/dist';

@Injectable({ providedIn: 'root' })
export class DecayBalanceService extends ObservableStore<IStoreState> {
  private _actualObservations = 0;
  private _curtailmentArray = [];
  private _endPeriodArray = [];
  private _maxObservations = 0;

  private vintagesApiURL: string;

  private _loadedDecayCalibrationTypes: IDecayCalibrationType[];

  public canDeactivate = true;
  public decaysSetOnce = false;

  get actualObservations() {
    return this._actualObservations;
  }

  get maxObservations() {
    return this._maxObservations;
  }

  get endPeriodArray() {
    return this._endPeriodArray;
  }

  get curtailmentArray() {
    return this._curtailmentArray;
  }

  private readonly excludeAcquisitionIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  get excludeAcquisitionIds(): number[] {
    return this.excludeAcquisitionIds$.getValue();
  }

  set excludeAcquisitionIds(ids: number[]) {
    if (ids) {
      ids.sort((a, b) => a - b);
      if (JSON.stringify(this.excludeAcquisitionIds) !== JSON.stringify(ids)) {
        this.excludeAcquisitionIds$.next(ids);
      }
    }
  }

  get onExcludedAcquisitionIdsChanged$() {
    return this.excludeAcquisitionIds$.asObservable();
  }

  private readonly startVintage$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // ! This method has been created to don't change the startVintage setter.
  // ! It avoids changing all the references to startVintage. We can unify It after beta release.
  set startVintage(startVintage: string) {
    if (startVintage && startVintage !== '0001-01-01T00:00:00') {
      if (this.startVintageManuallyChanged) {
        setTimeout(async () => {
          this.saveStartVintageOrExcludedAcquisitionChangesInLocalStorage(startVintage);
          this.startVintageManuallyChanged = false;
        }, 300);
      }
      this.startVintage$.next(startVintage);
    }
  }

  get startVintage(): string {
    return String(this.startVintage$.value);
  }

  public startVintageManuallyChanged = false;

  get onStartVintageChanged() {
    return this.startVintage$.asObservable();
  }

  public selectedDecayCalibrationType: IDecayCalibrationType;

  setSelectedDecayCalibrationType(id: number) {
    this.selectedDecayCalibrationType = this._loadedDecayCalibrationTypes.find(dt => dt.Id === id);
  }

  constructor(
    private readonly appConfig: AppConfigProvider,

    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly menuService: MenuService,

    private readonly decayCalibrationService: DecayCalibrationService,
    private readonly decayCalibrationTypeService: DecayCalibrationTypeService
  ) {
    super({ trackStateHistory: false });
    this.appConfig.environment.subscribe(env => {
      this.vintagesApiURL = `${env.API.DECAY}/api/decay/vintage`;
    });

    this.decayCalibrationTypeService.getDecayCalibrationTypes().subscribe(types => (this._loadedDecayCalibrationTypes = types));
  }

  private async saveStartVintageOrExcludedAcquisitionChangesInLocalStorage(startVintage: string) {
    const localStorageObject = this.localStorageService.fetchFromLocalStorage<IDecayCalibration>(
      LocalStorageKeys.Decays,
      this.menuService.selectedProduct.ProductId
    );

    // Get the calibration from localStorage or the latest calibration saved
    let calibration: IDecayCalibration = localStorageObject
      ? localStorageObject.payload
      : await this.decayCalibrationService.getLatestCalibrationForProduct(this.menuService.selectedProduct.ProductId).toPromise();

    if (!calibration) {
      calibration = clone({
        ...emptyDecayCalibration,
        RecordStatus: null
      });
    }

    // Set the start vintage and the excluded acquisitions (changes in both triggers this event)
    calibration.StartDate = startVintage;
    calibration.ExcludeAcquisitionIds = this.excludeAcquisitionIds;
    calibration.DecayCalibrationType = this.selectedDecayCalibrationType.Id;

    // Store it in local storage
    this.localStorageService.storeInLocalStorage(calibration, LocalStorageKeys.Decays, this.menuService.selectedProduct.ProductId);
    this.canDeactivate = false;
  }

  public getBalanceResult(query: IDecayQuery) {
    return this.http.post<IDecayResult>(this.vintagesApiURL, query).pipe(
      map(processDecayBalanceResult),
      map(filterNegativesFromResult),
      tap(balanceResult => {
        // Creating the end period array for use inside the estimate component
        this._actualObservations = balanceResult.ActualObservations;
        this._maxObservations = balanceResult.MaxObservations;
        this._endPeriodArray = createObservationsArray(
          balanceResult.LastContinuousValue ? balanceResult.LastContinuousValue.Observation : 0
        );
        this._curtailmentArray = createObservationsArray(this.maxObservations);
        this.decaysSetOnce = true;
        this.setState({ balanceResult }, StoreActions.SetDecays);
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: any) {
    console.error('Server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(errMessage);
    }
    return throwError(error || 'Server error');
  }
}
